/**
 * product-price-detail.js — Modale « Détail du prix » (fiche produit)
 *
 * Ouvre / ferme le <dialog class="product-price-detail"> (markup rendu côté
 * serveur par snippets/product-price-detail-modal.liquid, placé APRÈS
 * </sticky-element> par T15). Les 3 déclencheurs [data-price-detail-open]
 * (icône ⓘ, « Voir le détail », « Économisez X ») vivent dans le bloc prix,
 * DANS le sticky — donc PAS dans le dialog.
 *
 * Patron : IIFE + délégation sur `document` + garde d'attachement unique. On ne
 * met JAMAIS le dialog en cache global : on le retrouve au moment du clic. Aucun
 * `shopify:section:load/unload` — ce patron survit à tout re-render de section
 * (nouveau nœud = état neuf, anciens listeners GC avec l'ancien nœud).
 *
 * L'état (ouvert / déclencheur d'origine) est porté PAR le nœud <dialog>, jamais
 * en variable de module : un re-render repart d'un dialog propre, sans fuite.
 *
 * a11y : <dialog>.showModal() offre gratuitement top layer + piège clavier +
 * ::backdrop. Repli si showModal() absent (classe --fallback, cf. CSS T5), où on
 * garantit au minimum Esc + retour de focus.
 */

(function () {
  'use strict';

  // Idempotence : les listeners délégués ne s'attachent qu'une seule fois.
  if (window.__priceDetailBound) return;
  window.__priceDetailBound = true;

  // Lignes propres à une variante (une copie par variante, rendue côté serveur) : on
  // affiche celle de la variante choisie. Les variantes d'un autre produit (aperçu rapide)
  // ne trouvent aucune copie et ne changent rien.
  document.addEventListener('variant:change', function (e) {
    var variant = e && e.detail && e.detail.variant;
    if (!variant || !variant.id) return;
    var id = String(variant.id);
    document.querySelectorAll('.' + DIALOG).forEach(function (dialog) {
      var copies = dialog.querySelectorAll('[data-price-detail-variant]');
      var owns = Array.prototype.some.call(copies, function (el) {
        return el.getAttribute('data-price-detail-variant') === id;
      });
      if (!owns) return;
      copies.forEach(function (el) {
        el.hidden = el.getAttribute('data-price-detail-variant') !== id;
      });
    });
  });

  var DIALOG = 'product-price-detail';
  var FALLBACK = 'product-price-detail--fallback';

  // ---------------------------------------------------------------------------
  // findDialog(trigger)
  // Le dialog et les déclencheurs partagent le même wrapper `.shopify-section`
  // (le dialog est un frère du sticky, hors [data-product-blocks]). On scope à
  // la section pour ne jamais ouvrir la modale d'une autre instance. Repli : un
  // seul dialog dans le document ⇒ pas d'ambiguïté (aperçu éditeur, markup non
  // wrappé). D30 : en contexte quick-shop la modale n'est pas injectée ⇒ null
  // ⇒ no-op silencieux côté appelant.
  // ---------------------------------------------------------------------------
  function findDialog(trigger) {
    var scope = trigger.closest('.shopify-section');
    var dialog = scope && scope.querySelector('.' + DIALOG);
    if (!dialog) {
      var all = document.querySelectorAll('.' + DIALOG);
      if (all.length === 1) dialog = all[0];
    }
    return dialog;
  }

  // ---------------------------------------------------------------------------
  // wireDialog(dialog)
  // Câblage paresseux (au 1er open) des events qui NE BULLENT PAS (`close`) et ne
  // peuvent donc pas être délégués sur document. Flag posé sur le nœud : un
  // re-render crée un dialog vierge, recâblé à sa 1re ouverture.
  // ---------------------------------------------------------------------------
  function wireDialog(dialog) {
    if (dialog.__priceDetailWired) return;
    dialog.__priceDetailWired = true;
    // Esc natif et dialog.close() déclenchent l'event 'close' → nettoyage unique.
    dialog.addEventListener('close', function () {
      onClosed(dialog);
    });
  }

  // ---------------------------------------------------------------------------
  // openFor(trigger)
  // ---------------------------------------------------------------------------
  function openFor(trigger) {
    var dialog = findDialog(trigger);
    if (!dialog) return; // D30 : pas de modale (quick-shop) → no-op silencieux
    if (dialog.__priceDetailOpen) return; // déjà ouverte : pas de ré-ouverture

    dialog.__priceDetailTrigger = trigger; // pour restituer le focus à la fermeture
    wireDialog(dialog);

    // Feature-detect : natif → top layer + piège clavier + ::backdrop gratuits.
    if (typeof dialog.showModal === 'function') {
      try {
        dialog.showModal();
      } catch (e) {
        openFallback(dialog);
      }
    } else {
      openFallback(dialog);
    }
    dialog.__priceDetailOpen = true;

    // Focus initial sur le titre (cible de aria-labelledby, porte tabindex="-1").
    // Recherche DANS le dialog, jamais par id global codé en dur.
    var title = dialog.querySelector('[id^="PriceDetailTitle"]') || dialog.querySelector('[tabindex="-1"]');
    if (title && typeof title.focus === 'function') title.focus();
  }

  // Repli : ni top layer ni ::backdrop natifs → le CSS T5 sort le dialog du flux
  // via `.product-price-detail--fallback[open] { position: fixed; inset: 0 }`.
  function openFallback(dialog) {
    dialog.classList.add(FALLBACK);
    dialog.setAttribute('open', '');
  }

  // ---------------------------------------------------------------------------
  // closeDialog(dialog)
  // ---------------------------------------------------------------------------
  function closeDialog(dialog) {
    if (!dialog || !dialog.__priceDetailOpen) return;
    if (dialog.classList.contains(FALLBACK)) {
      // Mode repli : retrait manuel (close() natif absent) + nettoyage direct.
      dialog.removeAttribute('open');
      dialog.classList.remove(FALLBACK);
      onClosed(dialog);
    } else if (typeof dialog.close === 'function') {
      dialog.close(); // → event 'close' → onClosed()
    } else {
      dialog.removeAttribute('open');
      onClosed(dialog);
    }
  }

  // ---------------------------------------------------------------------------
  // onClosed(dialog) — idempotent : couvre Esc natif, .close(), croix, backdrop
  // et le chemin repli. Rend le focus au déclencheur d'origine.
  // ---------------------------------------------------------------------------
  function onClosed(dialog) {
    if (!dialog || !dialog.__priceDetailOpen) return;
    dialog.__priceDetailOpen = false;
    var trigger = dialog.__priceDetailTrigger;
    dialog.__priceDetailTrigger = null;
    if (trigger && typeof trigger.focus === 'function') trigger.focus();
  }

  // ---------------------------------------------------------------------------
  // Délégation des clics (les clics bullent, contrairement à 'close'/'cancel').
  // Aucun stopPropagation : le tracking T18 écoute en phase bubble sur document.
  // ---------------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-price-detail-open]');
    if (opener) {
      openFor(opener);
      return;
    }
    var closer = e.target.closest('[data-price-detail-close]');
    if (closer) {
      closeDialog(closer.closest('.' + DIALOG));
      return;
    }
    // Clic backdrop : la cible est le <dialog> lui-même (le ::backdrop en fait partie).
    if (e.target.classList && e.target.classList.contains(DIALOG)) {
      closeDialog(e.target);
    }
  });

  // Esc en mode repli uniquement : le <dialog> natif gère Esc lui-même (→ 'close').
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.keyCode !== 27) return;
    var openFallbackDialog = document.querySelector('.' + FALLBACK + '[open]');
    if (openFallbackDialog) closeDialog(openFallbackDialog);
  });
})();
